import { UnitData } from '../api/units';
import { getMultiPicturesUrls } from '../components/overview_value';
import i18n from '../i18n';
import { OverviewTableViewDelegate, RowCellsContent } from '../screens/trial_facts';
import { el } from '../utils';
import { removeIfChild } from './dom';

interface RenderLargeMultiPicturesParams {
  urls: string[];
  content: RowCellsContent;
  rowIdx: number;
  colIdx: number;
  delegate: OverviewTableViewDelegate;
}

interface RenderSmallMediumPicturesParams {
  urls: string[];
  content: RowCellsContent;
  isMulti: boolean;
  picSize: 'small' | 'medium';
}

interface RenderPictureCellParams {
  valueCell: HTMLElement;
  content: RowCellsContent;
  value: any;
  type: 'picture' | 'multi_pictures';
  rowIdx: number;
  colIdx: number;
  delegate: OverviewTableViewDelegate;
  input: HTMLInputElement;
}

export const createSelectOption = (value: string, content: string, selected: boolean) => {
  let selectOption = document.createElement('option');

  selectOption.value = value;
  selectOption.textContent = content;
  selectOption.selected = selected;

  return selectOption;
};

export const createUnitDropdownOption = (unit: UnitData, selectedUnitId: string) => {
  return createSelectOption(
    unit.id.toString(),
    `${unit.name} (${unit.description})`,
    unit.id === selectedUnitId
  );
};

function ensurePictureThumbnailsAttached(
  valueCell: HTMLElement,
  content: RowCellsContent,
  input: HTMLInputElement
) {
  if (content.which !== 'picture_thumbnails') {
    removeIfChild(valueCell, content.text);
    removeIfChild(valueCell, content.link);
    removeIfChild(valueCell, input);

    valueCell.appendChild(content.pictureThumbnails);
    content.which = 'picture_thumbnails';
  }
}

function renderLargeSinglePicture({ urls, content }: { urls: string[]; content: RowCellsContent }) {
  content.pictureThumbnails.appendChild(
    makeThumbnail('overview-picture-thumb overview-picture-thumb--large', urls[0])
  );
}

function renderLargeMultiPictures({
  urls,
  content,
  rowIdx,
  colIdx,
  delegate,
}: RenderLargeMultiPicturesParams) {
  const hasNav = urls.length > 1 && !!delegate.getPictureIndex && !!delegate.setPictureIndex;

  const currentIdx = Math.min(delegate.getPictureIndex?.(rowIdx, colIdx) ?? 0, urls.length - 1);

  const largeWrap = el('div', 'overview-picture-large-wrap');

  if (hasNav) largeWrap.appendChild(makeNavButton('prev', currentIdx, urls, rowIdx, colIdx, delegate));

  largeWrap.appendChild(
    makeThumbnail('overview-picture-thumb overview-picture-thumb--large', urls[currentIdx])
  );

  if (hasNav) largeWrap.appendChild(makeNavButton('next', currentIdx, urls, rowIdx, colIdx, delegate));

  content.pictureThumbnails.appendChild(largeWrap);

  if (urls.length > 1) {
    content.pictureThumbnails.appendChild(makeCountMore(urls.length - 1));
  }
}

const MAX_THUMBNAILS_PREVIEW_MEDIUM = 6;
const MAX_THUMBNAILS_PREVIEW_SMALL = 8;

function renderSmallMediumPictures({ urls, content, isMulti, picSize }: RenderSmallMediumPicturesParams) {
  const maxThumbnails = picSize === 'small' ? MAX_THUMBNAILS_PREVIEW_SMALL : MAX_THUMBNAILS_PREVIEW_MEDIUM;
  const stack = el('div', 'overview-picture-thumbnails-stack');

  for (const url of urls.slice(0, maxThumbnails)) {
    stack.appendChild(makeThumbnail('overview-picture-thumb', url));
  }

  content.pictureThumbnails.appendChild(stack);

  if (isMulti && urls.length > maxThumbnails) {
    content.pictureThumbnails.appendChild(makeCountMore(urls.length - maxThumbnails));
  }
}

function makeThumbnail(className: string, url?: string) {
  const thumb = el('img', className) as HTMLImageElement;
  // Treat thumbnails as decorative images: use src/object-fit and empty alt for accessibility.
  thumb.alt = '';
  if (url) {
    thumb.src = url;
    thumb.loading = 'lazy';
    thumb.style.objectFit = 'cover';
  }
  return thumb;
}

function makeCountMore(count: number) {
  const more = el('span', 'overview-picture-thumb-more');
  more.textContent = `+ ${count}`;
  return more;
}

function makeNavButton(
  dir: 'prev' | 'next',
  currentIdx: number,
  urls: string[],
  rowIdx: number,
  colIdx: number,
  delegate: OverviewTableViewDelegate
) {
  const btn = el('button', `overview-picture-nav-btn overview-picture-nav-${dir}`);
  btn.type = 'button';
  // Provide an accessible name for screen readers and keyboard users.
  const label = dir === 'prev' ? i18n.t('Previous picture')() : i18n.t('Next picture')();
  btn.setAttribute('aria-label', label);
  btn.title = label;
  // Create the icon element without using innerHTML.
  const icon = el('i', 'material-icons');
  icon.textContent = dir === 'prev' ? 'chevron_left' : 'chevron_right';
  btn.appendChild(icon);
  if ((dir === 'prev' && currentIdx === 0) || (dir === 'next' && currentIdx === urls.length - 1)) {
    btn.classList.add('hidden');
  }

  btn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const nextIdx = dir === 'prev' ? currentIdx - 1 : currentIdx + 1;
    if (nextIdx < 0 || nextIdx >= urls.length) return;

    delegate.setPictureIndex?.(rowIdx, colIdx, nextIdx);
  };

  return btn;
}

export function renderPictureCell({
  valueCell,
  content,
  value,
  type,
  rowIdx,
  colIdx,
  delegate,
  input,
}: RenderPictureCellParams) {
  const picSize = delegate.pictureSize?.() ?? 'medium';
  const isMulti = type === 'multi_pictures';
  const urls = isMulti ? getMultiPicturesUrls(value) : [(value as any)?.url];
  const isLarge = picSize === 'large';

  content.pictureThumbnails.innerHTML = '';
  content.pictureThumbnails.className = `overview-picture-thumbnails overview-picture-thumbnails--${picSize}`;

  if (isLarge) {
    isMulti
      ? renderLargeMultiPictures({ urls, content, rowIdx, colIdx, delegate })
      : renderLargeSinglePicture({ urls, content });
  } else {
    renderSmallMediumPictures({ urls, content, isMulti, picSize });
  }

  ensurePictureThumbnailsAttached(valueCell, content, input);
}
